import Dexie, { type EntityTable } from 'dexie';

import type {
  EntropyAudit,
  MemoryEntity,
  ReadingRecord,
  SpreadDefinition,
  UserPreferences,
} from '@lilith-arcana-os/domain';

export interface ReadingCacheRecord {
  id?: number;
  reading: ReadingRecord;
  cachedAt: string;
}

export interface CardCacheRecord {
  cardId: number;
  slug: string;
  name: string;
  baseMeaning: string;
  updatedAt: string;
}

export interface SpreadCacheRecord {
  id: string;
  spread: SpreadDefinition;
  updatedAt: string;
}

export interface MemoryCacheRecord {
  id?: number;
  entity: MemoryEntity;
  updatedAt: string;
}

export interface SettingsCacheRecord {
  key: string;
  value: UserPreferences | Record<string, string | boolean | number>;
  updatedAt: string;
}

export interface SyncQueueRecord {
  id?: number;
  queueType: string;
  payload: Record<string, string | number | boolean | string[]>;
  status: 'pending' | 'processing' | 'failed' | 'done';
  retryCount: number;
  createdAt: string;
  updatedAt: string;
}

export interface AuditCacheRecord {
  seed: string;
  audit: EntropyAudit;
  updatedAt: string;
}

export class LilithArcanaDexie extends Dexie {
  public readings_cache!: EntityTable<ReadingCacheRecord, 'id'>;
  public cards_cache!: EntityTable<CardCacheRecord, 'cardId'>;
  public spreads_cache!: EntityTable<SpreadCacheRecord, 'id'>;
  public memory_cache!: EntityTable<MemoryCacheRecord, 'id'>;
  public settings_cache!: EntityTable<SettingsCacheRecord, 'key'>;
  public sync_queue!: EntityTable<SyncQueueRecord, 'id'>;
  public audit_cache!: EntityTable<AuditCacheRecord, 'seed'>;

  public constructor() {
    super('lilith_arcana_os');

    this.version(1).stores({
      readings_cache: '++id, reading.userId, reading.spreadId, reading.createdAt, cachedAt',
      cards_cache: 'cardId, slug, updatedAt',
      spreads_cache: 'id, updatedAt',
      memory_cache: '++id, entity.userId, entity.kind, entity.label, updatedAt',
      settings_cache: 'key, updatedAt',
      sync_queue: '++id, status, createdAt, updatedAt',
      audit_cache: 'seed, updatedAt',
    });
  }
}

export const lilithDexie = new LilithArcanaDexie();
